import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, CreditCard, Receipt, Wallet, ChevronRight, AlertCircle, ChevronDown } from 'lucide-react';
import { createProductSale, CreateProductSaleData, calculateProductSaleTax } from '../lib/productSales';
import { supabase } from '../lib/supabaseClient';
import { StockItem } from '../lib/stockItems';
import { Customer } from '../lib/customers';
import ProductSaleSuccessModal from './ProductSaleSuccessModal';

interface CartItem {
  product: StockItem;
  quantity: number;
}

interface RecordProductSalePaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  businessId: string;
  cart: CartItem[];
  total: number;
  selectedCustomer?: Customer | null;
  appliedDiscount?: {
    id?: string;
    code?: string;
    type: 'percentage' | 'fixed';
    value: number;
    description?: string;
    discountAmount: number;
    source: 'code' | 'custom';
  } | null;
  subtotal: number;
  discountAmount: number;
  taxAmount: number;
}

const paymentMethods = [
  {
    id: 'credit_card',
    name: 'Record Credit / Debit',
    icon: <CreditCard className="h-5 w-5 text-purple-500" />,
    description: 'Record credit or debit card payment'
  },
  {
    id: 'bank_transfer',
    name: 'Record Bank Transfer',
    icon: <Receipt className="h-5 w-5 text-green-500" />,
    description: 'Record direct bank transfer payment'
  },
  {
    id: 'cash',
    name: 'Record Cash',
    icon: <Wallet className="h-5 w-5 text-yellow-500" />,
    description: 'Record cash payment'
  }
];

export default function RecordProductSalePaymentModal({
  isOpen,
  onClose,
  onSuccess,
  businessId,
  cart,
  total,
  selectedCustomer: propSelectedCustomer,
  appliedDiscount,
  subtotal,
  discountAmount,
  taxAmount
}: RecordProductSalePaymentModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedMethod, setSelectedMethod] = useState(paymentMethods[0]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  // Use the customer passed from parent (SellProductsModal)
  const selectedCustomer = propSelectedCustomer;
  
  // Success modal state
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [completedSale, setCompletedSale] = useState<{
    saleNumber: string;
    saleData: {
      subtotal: number;
      taxAmount: number;
      total: number;
      items: Array<{
        name: string;
        quantity: number;
        price: number;
      }>;
    };
  } | null>(null);

  // Use the tax calculation from the cart instead of recalculating
  const taxCalculation = React.useMemo(() => {
    return {
      subtotal: subtotal,
      tax_rate: 5, // 5% GST
      tax_amount: taxAmount,
      total: total,
      items: cart.map(item => ({
        price: item.product.price,
        quantity: item.quantity,
        tax_exempt: false,
        tax_rate: 5,
        tax_amount: (item.product.price * item.quantity * 5) / 100,
        total_price: item.product.price * item.quantity * 1.05
      }))
    };
  }, [cart, subtotal, taxAmount, total]);

  useEffect(() => {
    console.log('💳 RecordProductSalePaymentModal props changed:', {
      isOpen,
      businessId,
      cartLength: cart.length,
      total,
      taxCalculation
    });
    if (isOpen) {
      console.log('💳 Payment modal opening...');
      setIsAnimating(true);
    }
  }, [isOpen, businessId, cart.length, total, taxCalculation]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setIsDropdownOpen(false);
    };

    if (isDropdownOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [isDropdownOpen]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setError(null);
      setSelectedMethod(paymentMethods[0]);
      setIsDropdownOpen(false);
      setReference('');
      setNotes('');
      setShowSuccessModal(false);
      setCompletedSale(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!businessId || cart.length === 0) return;

    try {
      setLoading(true);
      setError(null);

      console.log('🎯 Creating product sale with payment');

      // Prepare sale data with tax calculations and discount
      const saleData: CreateProductSaleData = {
        business_id: businessId,
        customer_id: selectedCustomer?.id, // Add customer if selected
        subtotal_amount: taxCalculation.subtotal,
        tax_rate: taxCalculation.tax_rate,
        tax_amount: taxCalculation.tax_amount,
        total_amount: taxCalculation.total,
        discount_amount: discountAmount > 0 ? discountAmount : undefined,
        discount_details: appliedDiscount ? [{
          id: appliedDiscount.id || 'custom',
          title: appliedDiscount.source === 'code' ? `Code: ${appliedDiscount.code}` : (appliedDiscount.description || 'Custom Discount'),
          type: appliedDiscount.type,
          amount: appliedDiscount.value
        }] : undefined,
        original_subtotal: discountAmount > 0 ? subtotal + discountAmount : undefined,
        payment_status: 'paid', // Mark as paid since we're processing payment
        payment_method: selectedMethod.id,
        payment_date: new Date().toISOString(),
        notes: notes || `Payment via ${selectedMethod.name}${reference ? ` - Ref: ${reference}` : ''}${selectedCustomer ? ` - Customer: ${selectedCustomer.first_name} ${selectedCustomer.last_name}` : ''}${appliedDiscount ? ` - Discount: ${appliedDiscount.source === 'code' ? appliedDiscount.code : appliedDiscount.description}` : ''}`,
        items: cart.map((item, index) => {
          const taxItem = taxCalculation.items[index];
          return {
            stock_item_id: item.product.id,
            product_name: item.product.name,
            quantity: item.quantity,
            unit_price: item.product.price,
            tax_exempt: taxItem.tax_exempt,
            tax_rate: taxItem.tax_rate,
            tax_amount: taxItem.tax_amount,
            total_price: taxItem.total_price
          };
        })
      };

      console.log('📦 Sale data:', saleData);

      // Create the sale (this will also update stock quantities)
      const sale = await createProductSale(saleData);

      console.log('✅ Product sale created successfully:', sale);

      // Prepare success modal data
      setCompletedSale({
        saleNumber: sale.sale_number,
        saleData: {
          subtotal: taxCalculation.subtotal,
          taxAmount: taxCalculation.tax_amount,
          total: taxCalculation.total,
          discountAmount: discountAmount > 0 ? discountAmount : undefined,
          discountDetails: appliedDiscount ? {
            source: appliedDiscount.source,
            code: appliedDiscount.code,
            description: appliedDiscount.description,
            type: appliedDiscount.type,
            value: appliedDiscount.value
          } : undefined,
          items: cart.map(item => ({
            name: item.product.name,
            quantity: item.quantity,
            price: item.product.price
          }))
        }
      });

      // Show success modal
      setShowSuccessModal(true);
    } catch (error) {
      console.error('❌ Failed to create product sale:', error);
      setError(error instanceof Error ? error.message : 'Failed to process payment');
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    setCompletedSale(null);
    onSuccess();
    onClose();
  };

  if (!isOpen && !isAnimating) return null;

  const modalContent = (
    <div 
      className={`fixed inset-0 bg-black transition-opacity duration-300 ${
        isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
      } z-[100030]`}
      onClick={onClose}
    >
      <div 
        className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out shadow-2xl ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        onTransitionEnd={handleTransitionEnd}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col">
          {/* Header - Clean Modern Design */}
          <div className="bg-gray-900 border-b border-gray-800">
            <div className="flex items-center justify-between px-6 py-4">
              <div className="flex items-center space-x-3">
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
                <h2 className="text-xl font-semibold text-white">
                  Record Payment
                </h2>
              </div>
            </div>
          </div>

          <form 
            onSubmit={handleSubmit} 
            className="flex-1 overflow-y-auto bg-gray-50"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            <div className="space-y-0">
              {error && (
                <div className="bg-red-50 border-b border-red-200 text-red-700 px-6 py-4 text-sm">
                  <div className="flex items-center">
                    <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                    {error}
                  </div>
                </div>
              )}

              {/* Sale Summary */}
              <div className="bg-white border-b border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Sale Summary</h3>
                <div className="space-y-2 mb-4">
                  {cart.map((item, index) => (
                    <div key={index} className="flex justify-between items-center text-sm">
                      <span className="text-gray-700">
                        {item.product.name} × {item.quantity}
                      </span>
                      <span className="font-medium text-gray-900">
                        ${(item.product.price * item.quantity).toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="border-t border-gray-200 pt-3 space-y-2">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">Subtotal</span>
                    <span className="font-medium text-gray-900">
                      ${taxCalculation.subtotal.toFixed(2)}
                    </span>
                  </div>
                  {appliedDiscount && discountAmount > 0 && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-green-600">
                        Discount {appliedDiscount.source === 'code' ? `(${appliedDiscount.code})` : '(Custom)'}
                      </span>
                      <span className="font-medium text-green-600">
                        -${discountAmount.toFixed(2)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">GST ({taxCalculation.tax_rate}%)</span>
                    <span className="font-medium text-gray-900">
                      ${taxCalculation.tax_amount.toFixed(2)}
                    </span>
                  </div>
                  <div className="border-t border-gray-200 pt-2 flex justify-between items-center">
                    <span className="text-lg font-semibold text-gray-900">Total Amount</span>
                    <span className="text-lg font-bold text-green-600">
                      ${taxCalculation.total.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Payment Method */}
              <div className="bg-white border-b border-gray-200 p-6">
                <label className="block text-lg font-semibold text-gray-900 mb-4">
                  Payment Method
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsDropdownOpen(!isDropdownOpen);
                    }}
                    className="w-full flex items-center p-4 border-2 border-blue-500 bg-blue-50 rounded-lg transition-colors"
                  >
                    <div className="h-12 w-12 rounded-lg flex items-center justify-center mr-4 bg-blue-100">
                      {selectedMethod.icon}
                    </div>
                    <div className="flex-1 text-left">
                      <div className="text-sm font-semibold text-gray-900">{selectedMethod.name}</div>
                      <div className="text-sm text-gray-600">{selectedMethod.description}</div>
                    </div>
                    <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform ${
                      isDropdownOpen ? 'rotate-180' : ''
                    }`} />
                  </button>

                  {/* Dropdown Menu */}
                  {isDropdownOpen && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                      {paymentMethods.map((method, index) => (
                        <button
                          key={method.id}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedMethod(method);
                            setIsDropdownOpen(false);
                          }}
                          className={`w-full flex items-center p-3 hover:bg-gray-50 transition-colors ${
                            selectedMethod.id === method.id ? 'bg-blue-50' : ''
                          } ${index === 0 ? 'rounded-t-lg' : ''} ${
                            index === paymentMethods.length - 1 ? 'rounded-b-lg' : ''
                          }`}
                        >
                          <div className="h-8 w-8 rounded-lg flex items-center justify-center mr-3 bg-gray-100">
                            {method.icon}
                          </div>
                          <div className="flex-1 text-left">
                            <div className="text-sm font-medium text-gray-900">{method.name}</div>
                            <div className="text-xs text-gray-600">{method.description}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Payment Details */}
              <div className="bg-white p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Details</h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-2">
                      Reference Number
                    </label>
                    <input
                      type="text"
                      value={reference}
                      onChange={(e) => setReference(e.target.value)}
                      className="w-full px-4 py-3 text-base bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="Enter reference number (optional)"
                      style={{ 
                        minHeight: '48px',
                        fontSize: '16px'
                      }}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-2">
                      Notes
                    </label>
                    <textarea
                      rows={3}
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      className="w-full px-4 py-3 text-base bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                      placeholder="Add any additional notes about this payment..."
                      style={{ fontSize: '16px' }}
                    />
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={loading || cart.length === 0}
                    className={`w-full px-6 py-4 text-base font-bold text-white rounded-lg transition-colors ${
                      (!loading && cart.length > 0)
                        ? 'bg-blue-600 hover:bg-blue-700' 
                        : 'bg-gray-300 cursor-not-allowed'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                    style={{ minHeight: '52px' }}
                  >
                    {loading ? (
                      <div className="flex items-center justify-center">
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-3"></div>
                        Processing Payment...
                      </div>
                    ) : (
                      `Record Payment`
                    )}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {createPortal(modalContent, document.body)}
      
      {/* Success Modal */}
      {completedSale && (
        <ProductSaleSuccessModal
          isOpen={showSuccessModal}
          onClose={handleSuccessModalClose}
          saleNumber={completedSale.saleNumber}
          saleData={completedSale.saleData}
          customer={selectedCustomer}
          paymentMethod={selectedMethod.name}
        />
      )}
    </>
  );
}
